import { type FC, useCallback, useContext, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import {
  useGetWorkshopQuery,
  useUpgradeWorkshopMutation,
} from '@api/queries/workshop';
import { ConstructSucces } from '@common/components/ConstructSuccess/ConstructSuccess';
import { UpgradeBuild } from '@common/components/UpgradeBuild/UpgradeBuild';
import { useWorkshopUpgradedData } from '@common/hooks/build-upgraded-data';
import { useCheckRequirements } from '@common/hooks/check-requirements';
import { firebaseLogUpgradeBuilding } from '@common/lib/firebaseLog';
import { type FactoryTypeText } from '@common/types/factory';
import { ModalContext } from '@components/Modals/Modal/config';
import { updateStorageAndBalance } from '@redux/reducers/userReducer';
import { createRequirementsList } from '@redux/selectors/lib';
import { selectWorkshopByNameOrId } from '@redux/selectors/workshopSelectors';
import { useAppDispatch, useAppSelector } from '@redux/store';

import { completeNewLevelStep } from '@/common/lib/completeNewLevelStep';
import { SendAnalitics } from '@/common/lib/playdeck';
import { type GoodsType } from '@/common/types/goods';
import { selectNextStepPlant } from '@/redux/selectors/userSelectors';

const WorkshopUpgrade: FC = () => {
  const [skip, setSkip] = useState(false);
  useGetWorkshopQuery(undefined, { skip, refetchOnMountOrArgChange: true });
  const { id } = useParams();
  const { openModal } = useContext(ModalContext);
  const [isConstructComplete, setIsConstructComplete] = useState(false);
  const workshop = useAppSelector((state) =>
    selectWorkshopByNameOrId(state, id),
  );
  const [upgradeWorkshop, { isLoading }] = useUpgradeWorkshopMutation();
  const requirementsList = useMemo(
    () => createRequirementsList(workshop?.nextLevelRequirements),
    [workshop?.nextLevelRequirements],
  );
  const currentStep = useAppSelector(selectNextStepPlant);
  const dispatch = useAppDispatch();
  const { isComplete } = useCheckRequirements(workshop?.nextLevelRequirements);
  const compareData = useWorkshopUpgradedData(workshop?.id);
  const onQuarryUpgrade = useCallback((): void => {
    if (!workshop) return;
    const { id: item_id, name: item, level: item_level } = workshop;

    setSkip(true);
    upgradeWorkshop({ id: item_id }).then((response) => {
      SendAnalitics.buildDone({
        type: 'goods',
        item_id,
        item: item as GoodsType,
        item_level,
      });

      setIsConstructComplete(true);
      dispatch(updateStorageAndBalance(requirementsList));
      const level = response.data?.level;

      completeNewLevelStep({
        data: { name: response.data?.name, level: response.data?.level },
        dispatch,
        currentStep: {
          id: currentStep?.id,
          name: currentStep?.name,
          level: currentStep?.level,
        },
      });

      if (level && level % 5 === 0 && level < 16) {
        openModal({
          renderKind: 'openNewGoods',
          props: { id: workshop?.id as string },
        });
      }
    });

    if (workshop && workshop.nextLevelRequirements)
      firebaseLogUpgradeBuilding(
        {
          name: workshop.name,
          id: workshop.id,
          level: workshop.level + 1,
        },
        workshop.nextLevelRequirements,
      );
  }, [workshop?.id, upgradeWorkshop, requirementsList, dispatch, openModal]);

  if (!workshop || !requirementsList) {
    return null;
  }

  return isConstructComplete ? (
    <ConstructSucces
      name={workshop.name as FactoryTypeText}
      kind="workshop"
      id={workshop.id}
      level={workshop.level}
      compareData={compareData}
    />
  ) : (
    <>
      <Helmet>
        <title>Workshop_Upgrade</title>
      </Helmet>
      <UpgradeBuild
        name={workshop.name}
        id={workshop.id}
        level={workshop.level}
        kind="workshop"
        isComplete={isComplete}
        onUpgrade={onQuarryUpgrade}
        isLoading={isLoading}
        requirementsList={requirementsList}
      />
    </>
  );
};

export { WorkshopUpgrade };
